import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/api/client';
import type { SmtpAccount } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, TextInput, TextArea } from '@/components/ui/Field';
import styles from './Leads.module.css';

export function LeadComposeMail({ leadId, leadEmail }: { leadId: string; leadEmail: string }) {
  const qc = useQueryClient();
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [mailboxId, setMailboxId] = useState('');

  const accountsQ = useQuery({
    queryKey: ['smtp-accounts', projectId],
    enabled: Boolean(projectId && open),
    queryFn: () =>
      apiFetch<{ accounts: SmtpAccount[] }>(`/api/smtp-accounts?project_id=${projectId}`),
  });

  const precheckQ = useQuery({
    queryKey: ['mail-precheck', leadId, mailboxId],
    enabled: Boolean(open && leadId),
    queryFn: () => {
      const p = new URLSearchParams({ lead_id: leadId });
      if (mailboxId) p.set('smtp_account_id', mailboxId);
      return apiFetch<{ canSend: boolean; warnings: string[]; blockReason?: string }>(
        `/api/mail/send-precheck?${p}`,
      );
    },
  });

  const suggest = useMutation({
    mutationFn: () =>
      apiFetch<{ suggestion: string }>('/api/ai/suggest-opening', {
        method: 'POST',
        body: JSON.stringify({ lead_id: leadId, tone: 'friendly' }),
      }),
    onSuccess: (data) => {
      setBody((prev) => (prev.trim() ? `${data.suggestion}\n\n${prev}` : data.suggestion));
    },
  });

  const send = useMutation({
    mutationFn: () =>
      apiFetch('/api/mail/send', {
        method: 'POST',
        body: JSON.stringify({
          lead_id: leadId,
          smtp_account_id: mailboxId || undefined,
          subject,
          body_text: body,
        }),
      }),
    onSuccess: () => {
      setOpen(false);
      setSubject('');
      setBody('');
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    send.mutate();
  }

  if (!open) {
    return (
      <Button type="button" onClick={() => setOpen(true)}>
        Mail sturen naar {leadEmail}
      </Button>
    );
  }

  const accounts = accountsQ.data?.accounts ?? [];

  return (
    <form className={styles.composeMail} onSubmit={(e) => void submit(e)}>
      <h3>Mail compose</h3>
      {precheckQ.data?.warnings.length ? (
        <ul className={styles.muted}>
          {precheckQ.data.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      {accounts.length > 1 ? (
        <Field label="Mailbox">
          <select
            className={styles.filterInput}
            value={mailboxId}
            onChange={(e) => setMailboxId(e.target.value)}
          >
            <option value="">Standaard</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label} ({a.from_email})
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="Onderwerp">
        <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} required />
      </Field>
      <Field label="Bericht">
        <TextArea value={body} onChange={(e) => setBody(e.target.value)} required rows={6} />
      </Field>
      <p className={styles.signaturePreview}>
        Variabelen: {'{{voornaam}}'}, {'{{company}}'}, {'{{email}}'}. Handtekening/compliance uit
        mailbox of project.
      </p>
      <div className={styles.composeActions}>
        <Button
          type="button"
          variant="ghost"
          disabled={suggest.isPending}
          onClick={() => suggest.mutate()}
        >
          {suggest.isPending ? 'AI…' : 'AI opening'}
        </Button>
        <Button type="submit" disabled={send.isPending || precheckQ.data?.canSend === false}>
          In wachtrij (worker)
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
          Annuleren
        </Button>
      </div>
      {suggest.isError ? (
        <p role="alert" className={styles.muted}>
          {suggest.error instanceof ApiError && suggest.error.code === 'LLM_BUDGET_EXCEEDED'
            ? 'LLM-maandbudget bereikt voor dit project.'
            : suggest.error instanceof ApiError && suggest.error.code === 'AI_UNAVAILABLE'
              ? 'LLM niet beschikbaar — zet LLM_API_KEY of OPENROUTER_API_KEY op de server.'
              : 'AI-suggestie mislukt.'}
        </p>
      ) : null}
      {send.isError ? (
        <p role="alert">
          {send.error instanceof ApiError && send.error.code === 'MAILBOX_LIMIT'
            ? send.error.message
            : send.error instanceof ApiError
              ? send.error.message
              : 'Verzenden mislukt. Controleer mailbox en worker.'}
        </p>
      ) : null}
    </form>
  );
}
